import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { apiRequest } from "../../Api/Api";
import { ENDPOINTS } from "../../Api/Endpoints";
import { readRequestDraft, saveRequestDraft } from "./requestDraftSession";
import "./RequestFlow.scss";
import WizardSteps from "./WizardSteps";

const APPROX_LAT = "22.5726";
const APPROX_LNG = "88.3639";
const LAST_COORDS_KEY = "healbid:patient:lastCoords";

function uiToApiScope(ui) {
  if (ui === "pincode") return "locationWise";
  if (ui === "westBengal") return "allOverWestBengal";
  return "withinZone";
}

function apiToUiScope(api) {
  if (api === "locationWise") return "pincode";
  if (api === "allOverWestBengal") return "westBengal";
  if (api === "withinZone") return "withinZone";
  return "withinZone";
}

export default function RequestBudget() {
  const { id } = useParams();
  const navigate = useNavigate();
  const geoInFlight = useRef(false);
  const [budget, setBudget] = useState("");
  const [locationScopeUi, setLocationScopeUi] = useState("withinZone");
  const [radiusKm, setRadiusKm] = useState(20);
  const [pincode, setPincode] = useState("");
  const [patientLat, setPatientLat] = useState("");
  const [patientLng, setPatientLng] = useState("");
  const [usingApprox, setUsingApprox] = useState(false);
  const [locating, setLocating] = useState(false);
  const [locationHint, setLocationHint] = useState("");
  const [procedureOt, setProcedureOt] = useState(true);
  const [implants, setImplants] = useState(false);
  const [hospitalStay, setHospitalStay] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [procedureLabel, setProcedureLabel] = useState("");

  const hasRealGps = Boolean(patientLat && patientLng && !usingApprox);

  const requestCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setErr("Geolocation is not supported in this browser.");
      return;
    }
    if (geoInFlight.current) return;
    geoInFlight.current = true;
    setLocating(true);
    setErr("");
    setLocationHint("Asking for location permission…");

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = String(position.coords.latitude);
        const lng = String(position.coords.longitude);
        setPatientLat(lat);
        setPatientLng(lng);
        setUsingApprox(false);
        setLocationHint(`Using your current location · matching within ${radiusKm} km`);
        try {
          window.localStorage.setItem(LAST_COORDS_KEY, JSON.stringify({ patientLat: lat, patientLng: lng }));
        } catch (_) {}
        geoInFlight.current = false;
        setLocating(false);
      },
      (geoError) => {
        const code = Number(geoError?.code);
        if (code === 1) {
          setErr(
            "Location permission is blocked. Enable it in browser settings, or choose West Bengal / Pincode."
          );
        } else if (code === 3) {
          setErr("Location request timed out. Retry, or use approximate location.");
        } else {
          setErr("Unable to detect your location. Retry or use approximate / West Bengal / Pincode.");
        }
        setLocationHint("");
        geoInFlight.current = false;
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 12000 }
    );
  }, [radiusKm]);

  useEffect(() => {
    const draft = readRequestDraft(id);
    if (draft.budget || draft.affordabilityPrice) {
      setBudget(String(draft.budget || draft.affordabilityPrice || ""));
    }
    if (draft.locationScopeUi) setLocationScopeUi(String(draft.locationScopeUi));
    else if (draft.locationScope) setLocationScopeUi(apiToUiScope(draft.locationScope));
    if (draft.withinZoneRadiusKm) setRadiusKm(Number(draft.withinZoneRadiusKm) || 20);
    if (draft.searchPincode) setPincode(String(draft.searchPincode));
    if (draft.patientLat && draft.patientLng) {
      setPatientLat(String(draft.patientLat));
      setPatientLng(String(draft.patientLng));
      setUsingApprox(Boolean(draft.approximateLocation));
    } else {
      try {
        const raw = window.localStorage.getItem(LAST_COORDS_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        if (parsed?.patientLat && parsed?.patientLng) {
          setPatientLat(String(parsed.patientLat));
          setPatientLng(String(parsed.patientLng));
          setUsingApprox(false);
        }
      } catch (_) {}
    }
    if (draft.packageScope && typeof draft.packageScope === "object") {
      setProcedureOt(draft.packageScope.procedureOt !== false);
      setImplants(Boolean(draft.packageScope.implants));
      setHospitalStay(Boolean(draft.packageScope.hospitalStay));
    }
    if (Array.isArray(draft.selectedNames) && draft.selectedNames.length) {
      setProcedureLabel(draft.selectedNames.join(" › "));
    }

    if (!id || String(id).startsWith("local-")) return undefined;
    let cancel = false;
    (async () => {
      try {
        const res = await apiRequest({ method: "GET", url: ENDPOINTS.GET_MATCH_REQUEST_DETAIL(id) });
        const row = res?.request || null;
        if (!row || cancel) return;
        if (row.affordabilityPrice != null) setBudget((b) => b || String(row.affordabilityPrice));
        if (row.locationScope) setLocationScopeUi(apiToUiScope(row.locationScope));
        if (row.searchPincode) setPincode(String(row.searchPincode));
        if (row.patientLat != null && row.patientLng != null) {
          setPatientLat(String(row.patientLat));
          setPatientLng(String(row.patientLng));
          setUsingApprox(false);
        }
        if (row.withinZoneRadiusKm) setRadiusKm(Number(row.withinZoneRadiusKm) || 20);
        if (row.packageScope && typeof row.packageScope === "object") {
          setProcedureOt(row.packageScope.procedureOt !== false);
          setImplants(Boolean(row.packageScope.implants));
          setHospitalStay(Boolean(row.packageScope.hospitalStay));
        }
      } catch (_) {}
    })();
    return () => {
      cancel = true;
    };
  }, [id]);

  useEffect(() => {
    if (locationScopeUi === "withinZone" && !hasRealGps && !usingApprox) {
      requestCurrentLocation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectScope = (scope) => {
    setLocationScopeUi(scope);
    setErr("");
    setLocationHint("");
    if (scope === "withinZone") requestCurrentLocation();
  };

  const onContinue = async () => {
    const amount = String(budget || "").trim();
    if (!amount || !Number.isFinite(Number(amount))) {
      setErr("Enter a valid budget amount.");
      return;
    }
    if (locationScopeUi === "pincode") {
      const pin = String(pincode).trim();
      if (pin.length !== 6) {
        setErr("Enter a valid 6-digit pincode.");
        return;
      }
    }
    const lat = patientLat;
    const lng = patientLng;
    if (locationScopeUi === "withinZone") {
      if (!lat || !lng) {
        setErr("Near me needs your location. Allow permission, or use approximate / West Bengal / Pincode.");
        return;
      }
    }

    const apiScope = uiToApiScope(locationScopeUi);
    const packageScope = {
      procedureOt,
      implants,
      hospitalStay,
    };

    setSaving(true);
    setErr("");
    try {
      saveRequestDraft(id, {
        budget: amount,
        affordabilityPrice: amount,
        locationScopeUi,
        locationScope: apiScope,
        withinZoneRadiusKm: radiusKm,
        searchPincode: String(pincode).trim(),
        patientLat: apiScope === "withinZone" ? lat : "",
        patientLng: apiScope === "withinZone" ? lng : "",
        approximateLocation: apiScope === "withinZone" ? usingApprox : false,
        packageScope,
      });

      if (id && !String(id).startsWith("local-")) {
        await apiRequest({
          method: "PATCH",
          url: ENDPOINTS.UPDATE_MATCH_REQUEST(String(id)),
          data: {
            affordabilityPrice: amount,
            maxBudget: amount,
            locationScope: apiScope,
            packageScope,
            ...(apiScope === "withinZone"
              ? {
                  patientLat: lat,
                  patientLng: lng,
                  withinZoneRadiusKm: radiusKm,
                }
              : {}),
            ...(apiScope === "locationWise" ? { searchPincode: String(pincode).trim() } : {}),
          },
        });
      }
      navigate(`/request/${id}/details`);
    } catch (e) {
      setErr(e?.message || "Could not save budget.");
    } finally {
      setSaving(false);
    }
  };

  const nearMeHint = locating
    ? "Locating…"
    : usingApprox
      ? "Approx. area"
      : hasRealGps
        ? `${radiusKm} km`
        : "Needs GPS";

  return (
    <section className="reqWizard">
      <div className="reqWizard__card">
        <button
          type="button"
          className="reqWizard__back reqWizard__back--dupGlobalNav"
          onClick={() => navigate(`/request/${id}/category`)}
        >
          <ArrowLeft size={16} />
          Back
        </button>
        <p className="reqWizard__eyebrow">New care request</p>
        <h1 className="reqWizard__title">Draft surgery budget</h1>
        <p className="reqWizard__subtitle">
          Set where you want care and a draft amount. The match engine uses this location when doctors are notified.
        </p>
        <WizardSteps active={2} />

        {procedureLabel ? <p className="reqWizard__sectionSub">{procedureLabel}</p> : null}

        <h2 className="reqWizard__sectionTitle">Where do you want care?</h2>
        <p className="reqWizard__sectionSub">
          Near me uses GPS + radius. West Bengal covers the whole state. Pincode targets that pin and nearby area.
        </p>
        <div className="reqWizard__reviewGrid" style={{ display: "grid", gap: 8, marginBottom: 12 }}>
          {[
            { id: "withinZone", label: "Near me", hint: nearMeHint },
            { id: "westBengal", label: "West Bengal", hint: "All WB pins" },
            { id: "pincode", label: "Pincode", hint: "Specific area" },
          ].map((opt) => (
            <button
              key={opt.id}
              type="button"
              className={`reqWizard__ghost${locationScopeUi === opt.id ? " is-active" : ""}`}
              style={{
                justifyContent: "flex-start",
                borderColor: locationScopeUi === opt.id ? "#0aa9a9" : undefined,
                fontWeight: locationScopeUi === opt.id ? 700 : 500,
              }}
              onClick={() => selectScope(opt.id)}
            >
              {opt.label}
              <span style={{ marginLeft: 8, opacity: 0.7, fontWeight: 500 }}>{opt.hint}</span>
            </button>
          ))}
        </div>

        {locationScopeUi === "withinZone" ? (
          <div style={{ marginBottom: 16 }}>
            <label className="reqWizard__label" htmlFor="req-radius">
              Search radius: {radiusKm} km
            </label>
            <p className="reqWizard__sectionSub" style={{ marginTop: 0 }}>
              Only matching doctors/hospitals inside this radius see your budget.
            </p>
            <input
              id="req-radius"
              type="range"
              min={5}
              max={50}
              step={5}
              value={radiusKm}
              onChange={(e) => {
                const next = Number(e.target.value) || 20;
                setRadiusKm(next);
                if (hasRealGps || usingApprox) {
                  setLocationHint(
                    usingApprox
                      ? `Using approximate Kolkata centre · matching within ${next} km`
                      : `Using your current location · matching within ${next} km`
                  );
                }
              }}
              style={{ width: "100%" }}
            />
            {locationHint ? (
              <p className="reqWizard__sectionSub" style={{ color: "#0d6b5f", fontWeight: 600 }}>
                {locationHint}
              </p>
            ) : null}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                type="button"
                className="reqWizard__ghost"
                disabled={locating}
                onClick={() => requestCurrentLocation()}
              >
                {locating ? "Locating…" : "Use my location"}
              </button>
              <button
                type="button"
                className="reqWizard__ghost"
                disabled={locating}
                onClick={() => {
                  setPatientLat(APPROX_LAT);
                  setPatientLng(APPROX_LNG);
                  setUsingApprox(true);
                  setLocationHint(`Using approximate Kolkata centre · matching within ${radiusKm} km`);
                  setErr("");
                }}
              >
                Approximate
              </button>
            </div>
          </div>
        ) : null}

        {locationScopeUi === "westBengal" ? (
          <p className="reqWizard__sectionSub" style={{ marginBottom: 16 }}>
            Statewide: matching uses West Bengal providers (state or WB pincodes 700xxx–743xxx). No GPS needed.
          </p>
        ) : null}

        {locationScopeUi === "pincode" ? (
          <>
            <p className="reqWizard__sectionSub">
              Specific area: doctors with this pincode, plus those within about 15 km of the pin centre.
            </p>
            <label className="reqWizard__label" htmlFor="req-pin">
              6-digit pincode
            </label>
            <input
              id="req-pin"
              className="reqWizard__input"
              inputMode="numeric"
              maxLength={6}
              value={pincode}
              onChange={(e) => setPincode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="e.g. 700001"
            />
          </>
        ) : null}

        <h2 className="reqWizard__sectionTitle">Your budget</h2>
        <label className="reqWizard__label" htmlFor="req-budget">
          Amount (₹)
        </label>
        <input
          id="req-budget"
          className="reqWizard__input"
          inputMode="numeric"
          value={budget}
          onChange={(e) => {
            setBudget(e.target.value.replace(/\D/g, ""));
            setErr("");
          }}
          placeholder="e.g. 50000"
        />

        <h2 className="reqWizard__sectionTitle">What’s included? (optional)</h2>
        <label className="reqWizard__label" style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input type="checkbox" checked={procedureOt} onChange={(e) => setProcedureOt(e.target.checked)} />
          Procedure / OT fees
        </label>
        <label className="reqWizard__label" style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input type="checkbox" checked={implants} onChange={(e) => setImplants(e.target.checked)} />
          Implants / consumables
        </label>
        <label className="reqWizard__label" style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input type="checkbox" checked={hospitalStay} onChange={(e) => setHospitalStay(e.target.checked)} />
          Hospital stay
        </label>

        {err ? <p className="reqWizard__error">{err}</p> : null}

        <div className="reqWizard__actions">
          <button type="button" className="reqWizard__ghost" onClick={() => navigate(`/request/${id}/category`)}>
            Back
          </button>
          <button
            type="button"
            className="reqWizard__primary"
            disabled={saving || locating}
            onClick={() => void onContinue()}
          >
            {saving ? "Saving…" : "Continue"}
            <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </section>
  );
}
